import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { useQueryClient } from '@tanstack/react-query'
import * as yup from 'yup'

// FPCC
import SubmitButtons from 'components/Form/SubmitButtons'
import FileUploadField from 'components/Form/FileUploadField'
import useEditForm from 'common/hooks/useEditForm'
import { definitions } from 'common/utils/validationHelpers'
import { usePeople } from 'common/dataHooks/usePeople'
import { SUPPORTED_AUDIO_EXTENSIONS, AUDIO_PATH } from 'common/constants'
import { useSiteStore } from 'context/SiteContext'
import { useAudioCreate } from 'common/dataHooks/useAudio'
import AudioBaseForm from 'components/AudioCrud/AudioBaseForm'
import LoadOrError from 'components/LoadOrError'

function AudioUploadForm({ setSelectedAudio }) {
  const { site } = useSiteStore()
  const [isUploading, setIsUploading] = useState(false)
  const [fileUploaded, setFileUploaded] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const errorRef = useRef(null)
  const queryClient = useQueryClient()
  const [attemptedFileType, setAttemptedFileType] = useState('unknown')

  const validator = yup.object().shape({
    title: definitions.title().required('A title is required'),
    acknowledgement: definitions.paragraph(),
    description: definitions.paragraph(),
    audioFile: definitions
      .file({ SUPPORTED_AUDIO_EXTENSIONS })
      .required('A file is required'),
  })

  const defaultValues = {
    title: '',
    acknowledgement: '',
    description: '',
    includeInGames: 'true',
    includeInKids: 'true',
    isShared: false,
    audioFile: null,
    speakers: [],
  }

  const { control, errors, handleSubmit, register, reset } = useEditForm({
    defaultValues,
    validator,
    dataToEdit: {},
  })

  const speakerInfiniteQueryResponse = usePeople()

  const speakerOptions =
    speakerInfiniteQueryResponse?.data?.pages?.[0]?.results?.map((entry) => ({
      label: entry?.name,
      value: entry?.id,
    }))

  const { mutate } = useAudioCreate({
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: [AUDIO_PATH, site?.sitename, response?.id],
      })
      setIsUploading(false)
      setFileUploaded(true)
      setSelectedAudio(response)
    },
    onError: (error) => {
      setIsUploading(false)
      setUploadError(
        error?.response?.data?.message ||
          `Audio upload failed. Your input was '${attemptedFileType}' type. Supported file types are: .mp3, .wav, .opus, .oga, .weba.`,
      )
      setTimeout(() => {
        if (errorRef.current) {
          errorRef.current.scrollIntoView({ behavior: 'auto' }) // Jump to the error when it appears
        }
      }, 50)
    },
  })

  const submitHandler = (formData) => {
    if (!formData?.audioFile?.[0]) {
      return
    }
    const inputFileType =
      formData?.audioFile?.[0]?.name?.split('.').pop()?.toLowerCase() ||
      'unknown'
    const cleanInputFileType =
      inputFileType !== 'unknown' ? `.${inputFileType}` : inputFileType
    setAttemptedFileType(cleanInputFileType)
    setIsUploading(true)
    setUploadError(null)
    mutate(formData)
  }

  return fileUploaded ? (
    <div className="h-96 flex items-center justify-center">
      <div>
        <p>File successfully uploaded.</p>
        <p>Click insert above to add it to this dictionary entry.</p>
      </div>
    </div>
  ) : (
    <div id="AudioUploadForm" className="max-w-5xl pb-4 text-left mx-auto">
      <div ref={errorRef}>
        {uploadError && (
          <div className="bg-red-100 text-red-700 p-4 rounded-sm mb-4">
            <p>{uploadError}</p>
          </div>
        )}
      </div>
      <LoadOrError queryResponse={speakerInfiniteQueryResponse}>
        <form onReset={reset}>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12">
              <FileUploadField
                label="Choose audio file"
                nameId="audioFile"
                register={register}
                errors={errors}
              />
            </div>
            <AudioBaseForm
              register={register}
              errors={errors}
              control={control}
              speakerOptions={speakerOptions}
            />
            <div className="col-span-12 flex justify-end mt-2 px-6">
              {isUploading && (
                <SubmitButtons
                  submitLabel="Uploading .."
                  submitIcon="Upload"
                  onSubmitClick={null}
                />
              )}
              {!isUploading && !fileUploaded && (
                <SubmitButtons
                  submitLabel="Upload File"
                  submitIcon="Upload"
                  onSubmitClick={handleSubmit(submitHandler)}
                />
              )}
            </div>
          </div>
        </form>
      </LoadOrError>
    </div>
  )
}

// PROPTYPES
const { func } = PropTypes

AudioUploadForm.propTypes = {
  setSelectedAudio: func,
}

export default AudioUploadForm
